/**
 * Emotional analysis → three cards:
 *  - Тональность (sentiment: client start → end, delta, overall)
 *  - Вежливость (politeness score + violations)
 *  - Чёткость (clarity score + issues)
 */

import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import {
  AnalysisSection,
  scoreTextColor,
  scoreBgColor,
  sentimentLabel,
  sentimentColor,
  type XMLAnalysisResult,
} from './analysis-utils';

interface EmotionalSectionProps {
  result: XMLAnalysisResult;
}

export function EmotionalSection({ result }: EmotionalSectionProps) {
  const { t } = useTranslation('records');

  const sentiment = result.emotional?.sentiment;
  const politeness = result.emotional?.politeness;
  const clarity = result.emotional?.clarity;

  if (!sentiment && !politeness && !clarity) return null;

  return (
    <>
      {/* Тональность */}
      {sentiment && (
        <AnalysisSection
          title={t('records.analysisPanel.sentimentAnalysis', 'Sentiment Analysis')}
          summary={
            sentiment.overall !== undefined ? (
              <span
                className={cn('text-xs font-semibold', sentimentColor(sentiment.overall))}
              >
                {sentimentLabel(sentiment.overall, t)}
              </span>
            ) : undefined
          }
        >
          <div className="rounded-md bg-bg-surface px-3 py-2.5">
            <div className="font-mono text-2xs uppercase tracking-wider text-fg-subtle">
              {t('records.analysisPanel.client', 'Client')}
            </div>
            <div className="mt-1 flex items-center gap-2 text-[13px]">
              <span className={sentimentColor(sentiment.clientStart)}>
                {sentimentLabel(sentiment.clientStart, t)}
              </span>
              <span className="text-fg-subtle">→</span>
              <span className={sentimentColor(sentiment.clientEnd)}>
                {sentimentLabel(sentiment.clientEnd, t)}
              </span>
            </div>
            {sentiment.delta !== undefined && (
              <div
                className={cn(
                  'mt-1 font-mono text-micro',
                  sentiment.delta >= 0 ? 'text-success' : 'text-danger'
                )}
              >
                {t('records.analysisPanel.delta', 'Delta:')}{' '}
                {sentiment.delta > 0 ? '+' : ''}
                {sentiment.delta.toFixed(1)} {sentiment.delta >= 0 ? '▲' : '▼'}
              </div>
            )}
          </div>
          {sentiment.overall !== undefined && (
            <div className="mt-2 flex items-center justify-between text-xs">
              <span className="text-fg-muted">
                {t('records.analysisPanel.overallSentiment', 'Overall')}
              </span>
              <span className={cn('font-semibold', sentimentColor(sentiment.overall))}>
                {sentimentLabel(sentiment.overall, t)}
              </span>
            </div>
          )}
        </AnalysisSection>
      )}

      {/* Вежливость */}
      {politeness && (
        <AnalysisSection
          title={t('records.analysisPanel.politeness', 'Politeness')}
          summary={
            <span
              className={cn(
                'font-mono text-xs font-semibold',
                scoreTextColor(politeness.score)
              )}
            >
              {politeness.score}
            </span>
          }
        >
          <div className="h-2 overflow-hidden rounded-full bg-bg-subtle">
            <div
              className={cn('h-full rounded-full', scoreBgColor(politeness.score))}
              style={{ width: `${politeness.score}%` }}
            />
          </div>
          {politeness.violations && politeness.violations.length > 0 && (
            <div className="mt-2 flex flex-col gap-0.5">
              {politeness.violations.map((v, idx) => (
                <div key={idx} className="flex items-start gap-1 text-xs text-danger">
                  <span>•</span>
                  <span>{v}</span>
                </div>
              ))}
            </div>
          )}
        </AnalysisSection>
      )}

      {/* Чёткость */}
      {clarity && (
        <AnalysisSection
          title={t('records.analysisPanel.clarity', 'Clarity')}
          summary={
            clarity.score !== undefined ? (
              <span
                className={cn(
                  'font-mono text-xs font-semibold',
                  scoreTextColor(clarity.score)
                )}
              >
                {clarity.score}
              </span>
            ) : undefined
          }
        >
          {clarity.score !== undefined && (
            <div className="h-2 overflow-hidden rounded-full bg-bg-subtle">
              <div
                className={cn('h-full rounded-full', scoreBgColor(clarity.score))}
                style={{ width: `${clarity.score}%` }}
              />
            </div>
          )}
          {clarity.issues && clarity.issues.length > 0 && (
            <div className="mt-2 flex flex-col gap-0.5">
              {clarity.issues.map((issue, idx) => (
                <div key={idx} className="flex items-start gap-1 text-xs text-warning">
                  <span>•</span>
                  <span>{issue}</span>
                </div>
              ))}
            </div>
          )}
        </AnalysisSection>
      )}
    </>
  );
}
